// pages/TraitsPage.tsx
import React, { useState, useEffect } from "react";
import { Trait, TraitCategory, TraitForm } from "../../types";
import { useApp } from "../../context/AppContext";
import { 
  getTraits, 
  createTrait, 
  updateTrait, 
  deleteTrait as deleteTraitService,
} from "../../services/traitService";

import { 
  getTraitCategories
} from "../../services/traitCategoryService";

import PageShell from "../../components/layout/PageShell";
import GlassCard from "../../components/ui/GlassCard";
import Button from "../../components/ui/Button";
import Modal from "../../components/ui/Modal";
import ConfirmDialog from "../../components/ui/ConfirmDialog";
import FormSection from "../../components/ui/FormSection";
import { Input, Select } from "../../components/ui/FormFields";
import { toast } from "react-hot-toast";

const BLANK_TRAIT: TraitForm = {
  name: "",
  categoryId: "",
  scaleType: "likert_5",
  minScore: 0,
  maxScore: 0,
  lowRangeMin: 0,
  lowRangeMax: 0,
  mediumRangeMin: 0,
  mediumRangeMax: 0,
  highRangeMin: 0,
  highRangeMax: 0,
  interpretationRules: {
    low: "",
    medium: "",
    high: ""
  },
  weightage: 0
};

const SCALE_TYPES = [
  { value: "likert_5", label: "Likert Scale (5-point)", description: "Strongly Disagree to Strongly Agree" },
  { value: "likert_7", label: "Likert Scale (7-point)", description: "More granular agreement scale" },
  { value: "binary", label: "Binary (Yes/No)", description: "Simple two-choice response" },
  { value: "numeric_1_10", label: "Numeric (1-10)", description: "Numerical rating scale" },
  { value: "percentage", label: "Percentage (0-100)", description: "Percentage-based scoring" }
];

const TraitsPage: React.FC = () => {
  const { setTraits: setLocalTraits } = useApp();
  const [traits, setTraits] = useState<Trait[]>([]);
  const [categories, setCategories] = useState<TraitCategory[]>([]);
  const [loading, setLoading] = useState(true);
  
  const [showModal, setShowModal] = useState(false);
  const [editTrait, setEditTrait] = useState<Trait | null>(null);
  const [deleteTrait, setDeleteTrait] = useState<Trait | null>(null);
  const [traitForm, setTraitForm] = useState<TraitForm>(BLANK_TRAIT);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [fetchedTraits, fetchedCategories] = await Promise.all([
        getTraits(),
        getTraitCategories()
      ]);
      setTraits(fetchedTraits);
      setCategories(fetchedCategories);
      setLocalTraits(fetchedTraits);
    } catch (error) {
      console.error("Error loading data:", error);
      toast.error("Failed to load traits data");
    } finally {
      setLoading(false);
    }
  };

  const handleTraitFormChange = (k: keyof TraitForm) => (v: any) => {
    const value = (k === "minScore" || k === "maxScore" || 
                   k === "lowRangeMin" || k === "lowRangeMax" ||
                   k === "mediumRangeMin" || k === "mediumRangeMax" ||
                   k === "highRangeMin" || k === "highRangeMax" || 
                   k === "weightage") 
      ? parseFloat(v) || 0
      : v;
    setTraitForm(f => ({ ...f, [k]: value }));
  };

  const handleInterpretationChange = (level: "low" | "medium" | "high") => (value: string) => {
    setTraitForm(f => ({
      ...f,
      interpretationRules: { ...f.interpretationRules, [level]: value }
    }));
  };

  const openNewTrait = () => {
    setEditTrait(null);
    setTraitForm({ 
      ...BLANK_TRAIT, 
      categoryId: categories[0]?.id || "",
      minScore: 0,
      maxScore: 0,
      lowRangeMin: 0,
      lowRangeMax: 0,
      mediumRangeMin: 0,
      mediumRangeMax: 0,
      highRangeMin: 0,
      highRangeMax: 0,
      weightage: 0
    });
    setShowModal(true);
  };

  const openEditTrait = (t: Trait) => {
    setEditTrait(t);
    setTraitForm({
      name: t.name,
      categoryId: t.categoryId,
      scaleType: t.scaleType,
      minScore: t.minScore,
      maxScore: t.maxScore,
      lowRangeMin: t.lowRangeMin,
      lowRangeMax: t.lowRangeMax,
      mediumRangeMin: t.mediumRangeMin,
      mediumRangeMax: t.mediumRangeMax,
      highRangeMin: t.highRangeMin,
      highRangeMax: t.highRangeMax,
      interpretationRules: t.interpretationRules,
      weightage: t.weightage
    });
    setShowModal(true);
  };

  const saveTrait = async () => {
    if (!traitForm.name.trim()) {
      toast.error("Trait name is required");
      return;
    }
    if (!traitForm.categoryId) {
      toast.error("Please select a category");
      return;
    }
    if (traitForm.minScore >= traitForm.maxScore) {
      toast.error("Minimum score must be less than maximum score");
      return;
    }
    if (traitForm.lowRangeMax >= traitForm.mediumRangeMin) {
      toast.error("Low range max must be less than medium range min");
      return;
    }
    if (traitForm.mediumRangeMax >= traitForm.highRangeMin) {
      toast.error("Medium range max must be less than high range min");
      return;
    }

    try {
      if (editTrait) {
        await updateTrait(editTrait.id, traitForm);
        toast.success("Trait updated successfully");
      } else {
        await createTrait(traitForm);
        toast.success("Trait created successfully");
      }
      await loadData();
      setShowModal(false);
    } catch (error) {
      console.error("Error saving trait:", error);
      toast.error("Failed to save trait");
    }
  };

  const confirmDeleteTrait = async () => {
    if (!deleteTrait) return;
    try {
      await deleteTraitService(deleteTrait.id);
      toast.success("Trait deleted successfully");
      await loadData();
      setDeleteTrait(null);
    } catch (error) {
      console.error("Error deleting trait:", error);
      toast.error("Failed to delete trait");
    }
  };

  const getCategoryName = (categoryId: string) => {
    const category = categories.find(c => c.id === categoryId);
    return category?.name || "Uncategorized";
  };

  return (
    <PageShell 
      title="Traits Library" 
      subtitle="Manage psychometric traits for personality assessment"
      action={{ label: "Add Trait", onClick: openNewTrait }}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {traits.map((t) => (
          <GlassCard key={t.id} className="p-5 hover:shadow-lg transition-all duration-300 group">
            <div className="flex flex-col gap-3">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                    {getCategoryName(t.categoryId)}
                  </p>
                  <h3 className="font-extrabold text-gray-800 mt-0.5">{t.name}</h3>
                </div>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button size="sm" variant="ghost" onClick={() => openEditTrait(t)}>✏️</Button>
                  <Button size="sm" variant="ghost" onClick={() => setDeleteTrait(t)}>🗑</Button>
                </div>
              </div>
              
              <div className="flex flex-col gap-2 mt-2">
                <div className="flex gap-2 flex-wrap">
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                    Low: {t.lowRangeMin}–{t.lowRangeMax}
                  </span>
                  <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full">
                    Medium: {t.mediumRangeMin}–{t.mediumRangeMax}
                  </span>
                  <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">
                    High: {t.highRangeMin}–{t.highRangeMax}
                  </span>
                </div>
                {t.weightage > 0 && (
                  <span className="text-xs text-gray-500">Weightage: {t.weightage}</span>
                )}
              </div>
            </div>
          </GlassCard>
        ))}
      </div>

      {traits.length === 0 && !loading && (
        <GlassCard className="p-12 text-center">
          <div className="text-6xl mb-4">🧠</div>
          <p className="text-gray-500">No traits yet. Click "Add Trait" to create your first psychometric trait.</p>
        </GlassCard>
      )}

      <Modal isOpen={showModal} onClose={() => setShowModal(false)} 
             title={editTrait ? "Edit Trait" : "Create New Trait"} >
        <div className="space-y-5 max-h-[80vh] overflow-y-auto px-1 custom-scrollbar">
          
          <FormSection title="Basic Information" icon="📝">
            <div className="grid grid-cols-1 gap-4">
              <Select 
                label="Category" 
                value={traitForm.categoryId} 
                onChange={handleTraitFormChange("categoryId")} 
                required
              >
                <option value="">Select a category</option>
                {categories.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </Select>
              <Input 
                label="Trait Name" 
                value={traitForm.name} 
                onChange={handleTraitFormChange("name")} 
                required 
                placeholder="e.g., Emotional Intelligence, Stress Tolerance, Leadership" 
              />
            </div>
          </FormSection>

          <FormSection title="Scale Mapping" icon="📊">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Select 
                  label="Scale Type" 
                  value={traitForm.scaleType} 
                  onChange={handleTraitFormChange("scaleType")}
                >
                  {SCALE_TYPES.map(s => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </Select>
              </div>
              <div>
                <Input 
                  label="Minimum Score" 
                  type="number" 
                  value={String(traitForm.minScore)} 
                  onChange={handleTraitFormChange("minScore")}
                  placeholder="0"
                />
              </div>
              <div>
                <Input 
                  label="Maximum Score" 
                  type="number" 
                  value={String(traitForm.maxScore)} 
                  onChange={handleTraitFormChange("maxScore")}
                  placeholder="0"
                />
              </div>
            </div>
          </FormSection>

          <FormSection title="Score Ranges" icon="🎯">
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Input 
                  label="Low Range Min" 
                  type="number" 
                  value={String(traitForm.lowRangeMin)} 
                  onChange={handleTraitFormChange("lowRangeMin")}
                  placeholder="0"
                  step="0.1"
                />
                <Input 
                  label="Low Range Max" 
                  type="number" 
                  value={String(traitForm.lowRangeMax)} 
                  onChange={handleTraitFormChange("lowRangeMax")}
                  placeholder="0"
                  step="0.1"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Input 
                  label="Medium Range Min" 
                  type="number" 
                  value={String(traitForm.mediumRangeMin)} 
                  onChange={handleTraitFormChange("mediumRangeMin")}
                  placeholder="0"
                  step="0.1"
                />
                <Input 
                  label="Medium Range Max" 
                  type="number" 
                  value={String(traitForm.mediumRangeMax)} 
                  onChange={handleTraitFormChange("mediumRangeMax")}
                  placeholder="0"
                  step="0.1"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Input 
                  label="High Range Min" 
                  type="number" 
                  value={String(traitForm.highRangeMin)} 
                  onChange={handleTraitFormChange("highRangeMin")}
                  placeholder="0"
                  step="0.1"
                />
                <Input 
                  label="High Range Max" 
                  type="number" 
                  value={String(traitForm.highRangeMax)} 
                  onChange={handleTraitFormChange("highRangeMax")}
                  placeholder="0"
                  step="0.1"
                />
              </div>
            </div>
          </FormSection>

          <FormSection title="Interpretation Guidelines" icon="💡">
            <div className="space-y-4">
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <label className="text-sm font-semibold text-green-800 mb-2 block">Low Level Interpretation</label>
                <textarea
                  value={traitForm.interpretationRules.low}
                  onChange={(e) => handleInterpretationChange("low")(e.target.value)}
                  placeholder="What does a low score indicate? e.g., May struggle with..."
                  rows={3}
                  className="w-full px-3 py-2 border border-green-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white"
                />
              </div>
              <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                <label className="text-sm font-semibold text-yellow-800 mb-2 block">Medium Level Interpretation</label>
                <textarea
                  value={traitForm.interpretationRules.medium}
                  onChange={(e) => handleInterpretationChange("medium")(e.target.value)}
                  placeholder="What does a medium score indicate? e.g., Shows moderate ability in..."
                  rows={3}
                  className="w-full px-3 py-2 border border-yellow-200 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 bg-white"
                />
              </div>
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <label className="text-sm font-semibold text-red-800 mb-2 block">High Level Interpretation</label>
                <textarea
                  value={traitForm.interpretationRules.high}
                  onChange={(e) => handleInterpretationChange("high")(e.target.value)}
                  placeholder="What does a high score indicate? e.g., Demonstrates strong..."
                  rows={3}
                  className="w-full px-3 py-2 border border-red-200 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 bg-white"
                />
              </div>
            </div>
          </FormSection>

          <FormSection title="Scoring" icon="⚙️" className="hidden">
            <Input 
              label="Weightage" 
              type="number" 
              value={String(traitForm.weightage)} 
              onChange={handleTraitFormChange("weightage")} 
              placeholder="0"
              step="0.1"
            />
          </FormSection>

          <div className="flex gap-3 justify-end pt-4 border-t border-gray-200 sticky bottom-0 bg-white py-4">
            <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button onClick={saveTrait} className="bg-gradient-to-r from-indigo-600 to-purple-600">
              {editTrait ? "Update Trait" : "Create Trait"}
            </Button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog isOpen={!!deleteTrait} onClose={() => setDeleteTrait(null)}
        onConfirm={confirmDeleteTrait}
        title="Delete Trait" 
        message={`Are you sure you want to delete "${deleteTrait?.name}"?`} 
      />
    </PageShell>
  );
};

export default TraitsPage;